import clsx from 'clsx';
import Avatar from '@/components/comms/Avatar';
import type { InternalDmThread } from '@/domain/types';
import type { Person } from '@/utils/commsHelpers';
import { unreadInContainer } from '@/utils/commsHelpers';

interface Props {
  threads: InternalDmThread[];
  activeId: string | null;
  onPick: (id: string) => void;
  lastReadByChannel: Record<string, string>;
  selfPersonId: string | null;
  peopleById: Map<string, Person>;
}

function dmLabel(
  thread: InternalDmThread,
  selfId: string | null,
  peopleById: Map<string, Person>,
): { primaryPerson: Person | undefined; label: string } {
  const others = thread.participantIds.filter((id) => id !== selfId);
  if (others.length === 0) {
    const onlyMe = peopleById.get(thread.participantIds[0]!);
    return { primaryPerson: onlyMe, label: onlyMe?.displayName ?? 'You' };
  }
  if (others.length === 1) {
    const other = peopleById.get(others[0]!);
    return { primaryPerson: other, label: other?.displayName ?? others[0]! };
  }
  const names = others.map((id) => peopleById.get(id)?.displayName.split(' ')[0] ?? id);
  return { primaryPerson: peopleById.get(others[0]!), label: names.join(', ') };
}

export default function DmList({
  threads,
  activeId,
  onPick,
  lastReadByChannel,
  selfPersonId,
  peopleById,
}: Props) {
  return (
    <ul className="space-y-0.5" data-testid="comms-dm-list">
      {threads.map((t) => {
        const { primaryPerson, label } = dmLabel(t, selfPersonId, peopleById);
        const unread = unreadInContainer(t, lastReadByChannel[t.id], selfPersonId);
        const active = t.id === activeId;
        return (
          <li key={t.id}>
            <button
              type="button"
              onClick={() => onPick(t.id)}
              data-testid={`comms-dm-${t.id}`}
              className={clsx(
                'w-full flex items-center gap-2 px-3 py-1.5 text-left text-sm transition rounded-md',
                active
                  ? 'bg-gold/15 text-cream border-l-2 border-gold-bright'
                  : 'text-cream-soft hover:bg-ink-soft/50',
              )}
            >
              <Avatar person={primaryPerson} size="sm" />
              <span className="flex-1 truncate font-inter">{label}</span>
              {unread > 0 ? (
                <span
                  className="h-2 w-2 rounded-full bg-blood"
                  aria-label={`${unread} unread`}
                  data-testid={`comms-unread-${t.id}`}
                />
              ) : null}
            </button>
          </li>
        );
      })}
    </ul>
  );
}
